import { format, isValid, parseISO } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import TextSnippetRoundedIcon from "@mui/icons-material/TextSnippetRounded";
import { Chip, colors, Link as MuiLink, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams, GridSortModel } from "@mui/x-data-grid";
import { GridBaseColDef } from "@mui/x-data-grid/internals";

import {
  TableToolbar,
  TableToolbarLeft,
  TableToolbarRight,
  TableToolbarSearch,
  TableToolbarSelect,
  TableToolbarTitle,
} from "@components/TableToolbar/TableToolbar";

import C from "@constants/constants";

import TableOverlay from "@pages/Companies/components/TableOverlay";

import { usePatentsQuery } from "@queries/company.query";

import useSmartSearchStore from "@store/smartSearch";
import { useUserStore } from "@store/userStore";

import theme from "@theme/index";

import { getRowClassName, tableStyles } from "../Table/tableStyles";

import openTextRowPopup from "./utilities/openTextRowPopup";

interface IPatentTableProperties {
  companyId: string;
}

const defaultSelectedStatus = () => {
  const localStorageItem = localStorage.getItem("selectedStatus");

  if (!localStorageItem) {
    return ["Granted"];
  }

  const statusInLocalStorage = JSON.parse(localStorageItem) as string[];
  if (statusInLocalStorage) {
    return statusInLocalStorage;
  }
  return ["Granted"];
};

function PatentTable({ companyId }: IPatentTableProperties) {
  const user = useUserStore();
  const [query, setQuery] = useState("");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 100 });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "similarityScore", sort: "desc" }]);

  const [selectedStatus, setSelectedStatus] = useState<string[]>(["Granted"]);
  const parameters = useParams();

  useEffect(() => {
    setSelectedStatus(defaultSelectedStatus());
  }, []);

  const { isSmartSearchEnabled } = useSmartSearchStore();

  const location = useLocation();

  const { data, handlePatentStatus, handleSearch, isLoading } = usePatentsQuery({
    companyId,
    field: sortModel[0]?.field ?? "filingDate",
    limit: paginationModel.pageSize,
    page: paginationModel.page,
    searchId: isSmartSearchEnabled && user.currentSearch && !parameters.technologyId ? user.currentSearch : undefined,
    sort: sortModel[0]?.sort ?? "desc",
    technologyId: parameters.technologyId,
  });

  useEffect(() => {
    handleSearch(query);
  }, [query, handleSearch]);

  useEffect(() => {
    handlePatentStatus(selectedStatus);
  }, [selectedStatus, handlePatentStatus]);

  const handleSetSelectedStatus = (newValue: string[]) => {
    setSelectedStatus(newValue);
    localStorage.setItem("selectedStatus", JSON.stringify(newValue));
  };

  const columns: GridColDef[] = useMemo(() => {
    const baseColumns: GridBaseColDef[] = [
      {
        field: "status",
        headerName: "Status",
        renderCell: (parameters: GridRenderCellParams) => {
          const status = parameters.value === "Pending" ? "Application" : "Granted";
          return (
            <Chip
              label={status}
              size="small"
              sx={{
                backgroundColor: parameters.value === "Pending" ? colors.yellow["700"] : theme.palette.success.dark,
                color: parameters.value === "Pending" ? theme.palette.grey["800"] : theme.palette.common.white,
                fontWeight: 600,
              }}
            />
          );
        },
        width: 120,
      },
      {
        field: "publicationNumber",
        headerName: "Publication #",
        renderCell: (parameters: GridRenderCellParams) => (
          <Typography component="span" variant="body2">
            {parameters.value ? (
              <a href={parameters.row.url} rel="noopener noreferrer" target="_blank">
                {parameters.value}
              </a>
            ) : (
              "-"
            )}
          </Typography>
        ),
        sortable: false,
        width: 165,
      },
      {
        field: "applicationNumber",
        headerName: "Application #",
        renderCell: (parameters: GridRenderCellParams) => (
          <Typography component="span" variant="body2">
            {parameters.value}
          </Typography>
        ),
        sortable: false,
        width: 165,
      },
      {
        field: "filingDate",
        headerName: "Filing Date",
        renderCell: (parameters: GridRenderCellParams) => (
          <Typography component="span" variant="body2">
            {parameters.value}
          </Typography>
        ),
        sortable: true,
        valueGetter: (parameters: string) => {
          if (parameters) {
            const date = parseISO(parameters);
            if (isValid(date)) {
              return format(date, "MM/dd/yy");
            }
          }
          return "-";
        },
        width: 120,
      },
      {
        field: "abstract",
        headerName: "Abstract",
        renderCell: (parameters: GridRenderCellParams) =>
          parameters.value !== "NaN" && parameters.value ? (
            <MuiLink
              component="button"
              onClick={() => openTextRowPopup("Abstract Details", parameters.value)}
              style={{ alignItems: "center", display: "flex", gap: "8px" }}
            >
              <TextSnippetRoundedIcon fontSize="small" /> View
            </MuiLink>
          ) : (
            <Typography component="span" variant="body2">
              -
            </Typography>
          ),
        sortable: false,
        width: 90,
      },
      {
        field: "title",
        headerName: "Title",
        sortable: false,
        width: 1200,
      },
    ];

    if (isSmartSearchEnabled || location.pathname.includes("portfolio")) {
      const similarityColumn: GridBaseColDef = {
        field: "similarityScore",
        headerName: "Similarity Score",
        renderCell: (parameters: GridRenderCellParams) =>
          parameters.value !== "-" && parameters.value ? (
            <Chip
              color="primary"
              label={Number.parseFloat(Number(parameters.value).toFixed(C.SCORE_DECIMAL_PLACES))}
              size="small"
              sx={{
                color: "white",
                fontWeight: 600,
                px: 0.5,
              }}
            />
          ) : (
            <Typography component="span" variant="body2">
              -
            </Typography>
          ),
        valueGetter: (parameters?: number) => {
          return parameters || "-";
        },
        width: 140,
      };

      baseColumns.splice(1, 0, similarityColumn);
    }

    return baseColumns;
  }, [location.pathname]);

  const rows = useMemo(
    () =>
      data.patents.map((patent, index) => ({
        abstract: patent.abstract,
        applicationNumber: patent.displayApplicationNumber,
        filingDate: patent.filingDate,
        id: index,
        key: patent.publicationNumber,
        publicationNumber: patent.displayPublicationNumber,
        similarityScore: patent.similarityScore,
        status: patent.status,
        title: patent.title,
        url: patent.googlePatentsUrl,
      })),
    [data.patents],
  );

  return (
    <>
      <TableToolbar>
        <TableToolbarLeft>
          <TableToolbarTitle tableTitle="Patents" workflow="patent" />
        </TableToolbarLeft>
        <TableToolbarRight>
          <TableToolbarSearch placeholder="Search Patents..." query={query} setQuery={setQuery} />
          <TableToolbarSelect
            hasToHaveOneChecked
            onChange={(newValue) => handleSetSelectedStatus(newValue)}
            options={["Application", "Granted"]}
            value={selectedStatus}
          />
        </TableToolbarRight>
      </TableToolbar>
      <DataGrid
        columns={columns}
        disableRowSelectionOnClick
        getRowClassName={getRowClassName}
        initialState={{
          pagination: {
            paginationModel: {
              page: paginationModel.page,
              pageSize: paginationModel.pageSize,
            },
          },
          sorting: {
            sortModel,
          },
        }}
        loading={isLoading}
        onPaginationModelChange={(newPagePaginationModel) => {
          setPaginationModel(newPagePaginationModel);
        }}
        onSortModelChange={(newSortModel) => {
          setSortModel(newSortModel);
        }}
        pageSizeOptions={[25, 50, 100]}
        paginationMode="server"
        rowCount={data.totalRows}
        rows={rows}
        slots={{
          loadingOverlay: () => <TableOverlay subtitle="One moment please..." title="Loading Patents" />,
        }}
        sortingMode="server"
        sortingOrder={["asc", "desc"]}
        sortModel={sortModel}
        sx={{
          ...tableStyles.root,
          "&.MuiDataGrid-root": {
            borderTop: "none",
          },
          "&>.MuiDataGrid-main": {
            "&>.MuiDataGrid-columnHeaders": {
              borderBottom: "none",
            },
          },
          borderRadius: 0,
          height: "100%",
        }}
      />
    </>
  );
}

export default PatentTable;
